/**
 * Scrolls the page to the given element, optional X & Y offset can be provided, default values are `0`.
 * @param element HTML element or selector to scroll to
 * @param offsetY Vertical offset distance to the element
 * @param offsetX Horizontal offset distance to the element
 */
export function scrollToElement(element, offsetY, offsetX) {
    if (offsetY === void 0) { offsetY = 0; }
    if (offsetX === void 0) { offsetX = 0; }
    if (!element) {
        throw new Error('Must provide an element to scroll to.');
    }
    if (element instanceof HTMLElement) {
        window.scrollTo.apply(window, [element.offsetLeft + offsetX, element.offsetTop + offsetY]);
    }
    if (typeof element === 'string') {
        var query = document.querySelector(element);
        if (!query) {
            throw new Error('Cannot find element: ' + element + '.');
        }
        window.scrollTo.apply(window, [query.offsetLeft + offsetX, query.offsetTop + offsetY]);
    }
}
/**
 * Returns the visibility percentage of an element in relation to the viewport.
 * @param element Check visibility of this element
 * @return        Visibility percentage
 */
export function visiblePercentage(element) {
    var deltas = getDeltas(element);
    if (!deltas) {
        return 0;
    }
    return (deltas.left * deltas.right * deltas.top * deltas.bottom);
}
/**
 * Checks if the given element is visible in the viewport.
 * Allows to check for visibility percentage of the element.
 * @param element Check visibility of this element
 * @param x       X percentage of visibility, from 0 to 1
 * @param y       Y percentage of visibility, from 0 to 1
 * @return        Returns true if element is visible, otherwise returns false
 */
export function inViewport(element, x, y) {
    if (x === void 0) { x = 1; }
    if (y === void 0) { y = 1; }
    var deltas = getDeltas(element);
    if (!deltas) {
        return false;
    }
    return (deltas.left * deltas.right) >= x && (deltas.top * deltas.bottom) >= y;
}
/**
 * Returns the ElementBounds for an element
 * @param element Get deltas for this element
 * @return        Visibility percentage
 */
export function getDeltas(element) {
    var viewport = {
        top: window.pageYOffset,
        left: window.pageXOffset,
        right: 0,
        bottom: 0
    };
    viewport.right = viewport.left + window.innerWidth;
    viewport.bottom = viewport.top + window.innerHeight;
    var width = element.offsetWidth;
    var height = element.offsetHeight;
    var bound = getElementOffset(element);
    bound.right = bound.left + width;
    bound.bottom = bound.top + height;
    var visible = (!(viewport.right < bound.left ||
        viewport.left > bound.right ||
        viewport.bottom < bound.top ||
        viewport.top > bound.bottom));
    if (!visible) {
        return;
    }
    var deltas = {
        top: Math.min(1, (bound.bottom - viewport.top) / height),
        bottom: Math.min(1, (viewport.bottom - bound.top) / height),
        left: Math.min(1, (bound.right - viewport.left) / width),
        right: Math.min(1, (viewport.right - bound.left) / width)
    };
    return deltas;
}
/**
 * Returns the position rectangle for the element offset.
 * @param element  Get the offset of this element
 * @return         Position rectangle for the element
 */
export function getElementOffset(element) {
    var box = element.getBoundingClientRect();
    var docEl = element.ownerDocument;
    return {
        width: box.width || element.offsetWidth,
        height: box.height || element.offsetHeight,
        top: box.top + (window.pageYOffset || docEl.documentElement.scrollTop) - (docEl.documentElement.clientTop || 0),
        left: box.left + (window.pageXOffset || docEl.documentElement.scrollLeft) - (docEl.documentElement.clientLeft || 0)
    };
}
/**
 * Returns the position rectangle for the element.
 * @param element Get position rectangle of this element.
 * @return        The position rectangle for the given element.
 */
export function getElementPositionRectangle(element) {
    var elementOffset;
    var parentEl; // Document or HTMLElement
    var parentOffsetRect = { top: 0, left: 0 };
    if (getCss(element, 'position') === 'fixed') {
        elementOffset = element.getBoundingClientRect();
    }
    else {
        elementOffset = getElementOffset(element);
        parentEl = getParent(element);
        parentOffsetRect.top = Number(getCss(parentEl, 'borderTopWidth', true));
        parentOffsetRect.left = Number(getCss(parentEl, 'borderLeftWidth', true));
    }
    var marginTop = Number(getCss(element, 'marginTop', true));
    var marginLeft = Number(getCss(element, 'marginLeft', true));
    return {
        width: element.offsetWidth,
        height: element.offsetHeight,
        top: elementOffset.top - parentOffsetRect.top - marginTop,
        left: elementOffset.left - parentOffsetRect.left - marginLeft
    };
}
/**
 * Returns the parent element for the given element.
 * @param element Given element.
 * @return        Parent element.
 */
export function getParent(element) {
    var docEl = element.ownerDocument;
    var parentOffset = element.offsetParent || docEl;
    if (isNodeName(parentOffset, '#document')) {
        return docEl.documentElement;
    }
    while (parentOffset && !isNodeName(parentOffset, 'html') && getCss(parentOffset, 'position') !== 'static') {
        parentOffset = parentOffset.offsetParent;
    }
    return parentOffset || docEl.documentElement;
}
/**
 * Returns the given CSS property value.
 * @param element Given element.
 * @param prop    Style property to get.
 * @param parse   Parse css values as integers.
 * @return        Element CSS property value.
 */
export function getCss(element, prop, parse) {
    if (parse === void 0) { parse = false; }
    var style = window.getComputedStyle(element);
    var styleValue = style.getPropertyValue(prop) || '0';
    return parse ? String(parseInt(styleValue, 10)) || '0' : styleValue;
}
/**
 * Checks if the element `nodeName` property matches the given name.
 * @param element Given element
 * @param name    Node name to check
 * @return        Returns true if node name match to given name.
 */
export function isNodeName(element, name) {
    return element.nodeName && element.nodeName.toLowerCase() === name.toLowerCase();
}
